import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import API from "../lib/api";
import { Section } from "../components/Common";
import { toast } from "sonner";
import { errorMessage } from "../lib/errors";
import { useAuth } from "../lib/auth";

export default function BusinessRegisterPage() {
  const { t } = useTranslation();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({
    name: "", tagline: "", description: "", category: "therapists",
    services: "", languages_offered: "en", city: "", country: "US",
    contact_email: "", contact_phone: "", website: "", image: "",
  });
  const [sending, setSending] = useState(false);

  useEffect(() => { API.get("/marketplace/categories").then(({ data }) => setCategories(data.categories || [])); }, []);
  useEffect(() => { if (!authLoading && !user) navigate("/login"); }, [user, authLoading, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const payload = {
        ...form,
        services: form.services.split(",").map((s) => s.trim()).filter(Boolean),
        languages_offered: form.languages_offered.split(",").map((s) => s.trim().toLowerCase()).filter(Boolean),
      };
      const { data } = await API.post("/marketplace/register", payload);
      toast.success(t("marketplace.form.thanks"));
      navigate("/marketplace/my-business");
    } catch (e) { toast.error(errorMessage(e, "Could not submit")); } finally { setSending(false); }
  };

  if (authLoading) return <div className="app-container py-20 text-white/70">{t("common.loading")}</div>;
  if (!user) return null;

  return (
    <Section eyebrow="— Marketplace" title={t("marketplace.register_title")} subtitle={t("marketplace.register_sub")}>
      <form onSubmit={submit} className="max-w-2xl card-surface p-6 grid gap-3" data-testid="biz-register-form">
        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("marketplace.form.name")} required className="bg-[#0F1116] border border-white/10 rounded-lg px-4 py-3 text-sm" data-testid="biz-name" />
        <input value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} placeholder={t("marketplace.form.tagline")} className="bg-[#0F1116] border border-white/10 rounded-lg px-4 py-3 text-sm" data-testid="biz-tagline" />
        <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder={t("marketplace.form.description")} rows={5} required className="bg-[#0F1116] border border-white/10 rounded-lg px-4 py-3 text-sm" data-testid="biz-desc" />
        <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="bg-[#0F1116] border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="biz-cat">
          {categories.map((c) => <option key={c.slug} value={c.slug}>{c.title}</option>)}
        </select>
        <input value={form.services} onChange={(e) => setForm({ ...form, services: e.target.value })} placeholder={t("marketplace.form.services")} className="bg-[#0F1116] border border-white/10 rounded-lg px-4 py-3 text-sm" data-testid="biz-services" />
        <input value={form.languages_offered} onChange={(e) => setForm({ ...form, languages_offered: e.target.value })} placeholder={t("marketplace.form.languages")} className="bg-[#0F1116] border border-white/10 rounded-lg px-4 py-3 text-sm" />
        <div className="grid grid-cols-2 gap-2">
          <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder={t("marketplace.form.city")} required className="bg-[#0F1116] border border-white/10 rounded-lg px-4 py-3 text-sm" data-testid="biz-city" />
          <input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value.toUpperCase().slice(0,2) })} placeholder={t("marketplace.form.country")} required maxLength={2} className="bg-[#0F1116] border border-white/10 rounded-lg px-4 py-3 text-sm" data-testid="biz-country" />
        </div>
        <input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} placeholder={t("marketplace.form.email")} required className="bg-[#0F1116] border border-white/10 rounded-lg px-4 py-3 text-sm" data-testid="biz-email" />
        <input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} placeholder={t("marketplace.form.phone")} className="bg-[#0F1116] border border-white/10 rounded-lg px-4 py-3 text-sm" />
        <input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder={t("marketplace.form.website")} className="bg-[#0F1116] border border-white/10 rounded-lg px-4 py-3 text-sm" />
        <input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} placeholder={t("marketplace.form.image")} className="bg-[#0F1116] border border-white/10 rounded-lg px-4 py-3 text-sm" />
        <div>
          <button disabled={sending} className="btn-primary text-sm" data-testid="biz-submit">{sending ? "…" : t("marketplace.form.submit")}</button>
        </div>
      </form>
    </Section>
  );
}
